import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Link2, Octagon, Hand, Search, MessageCircle, Heart, Shield, ArrowRight, CheckCircle, History, ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const getExercises = (t) => [
  {
    id: "chain_analysis",
    name: t("tcdEx.chain.name"),
    description: t("tcdEx.chain.desc"),
    icon: Link2,
    color: "bg-teal-50 text-teal-600",
    prompts: [
      { key: "behavior", label: t("tcdEx.chain.behavior"), placeholder: t("tcdEx.chain.behaviorPh") },
      { key: "trigger", label: t("tcdEx.chain.trigger"), placeholder: t("tcdEx.chain.triggerPh") },
      { key: "vulnerability", label: t("tcdEx.chain.vulnerability"), placeholder: t("tcdEx.chain.vulnerabilityPh") },
      { key: "chain", label: t("tcdEx.chain.chain"), placeholder: t("tcdEx.chain.chainPh") },
      { key: "consequences", label: t("tcdEx.chain.consequences"), placeholder: t("tcdEx.chain.consequencesPh") },
      { key: "skills", label: t("tcdEx.chain.skills"), placeholder: t("tcdEx.chain.skillsPh") },
    ],
  },
  {
    id: "stop_tip",
    name: t("tcdEx.stop.name"),
    description: t("tcdEx.stop.desc"),
    icon: Octagon,
    color: "bg-red-50 text-red-600",
    prompts: [
      { key: "crisis", label: t("tcdEx.stop.crisis"), placeholder: t("tcdEx.stop.crisisPh") },
      { key: "stop_steps", label: t("tcdEx.stop.steps"), placeholder: t("tcdEx.stop.stepsPh") },
      { key: "tip_used", label: t("tcdEx.stop.tip"), placeholder: t("tcdEx.stop.tipPh") },
      { key: "outcome", label: t("tcdEx.stop.outcome"), placeholder: t("tcdEx.stop.outcomePh") },
    ],
  },
  {
    id: "radical_acceptance",
    name: t("tcdEx.acceptance.name"),
    description: t("tcdEx.acceptance.desc"),
    icon: Hand,
    color: "bg-blue-50 text-blue-600",
    prompts: [
      { key: "situation", label: t("tcdEx.acceptance.situation"), placeholder: t("tcdEx.acceptance.situationPh") },
      { key: "non_acceptance", label: t("tcdEx.acceptance.signs"), placeholder: t("tcdEx.acceptance.signsPh") },
      { key: "acceptance_steps", label: t("tcdEx.acceptance.steps"), placeholder: t("tcdEx.acceptance.stepsPh") },
      { key: "body_practice", label: t("tcdEx.acceptance.body"), placeholder: t("tcdEx.acceptance.bodyPh") },
      { key: "result", label: t("tcdEx.acceptance.result"), placeholder: t("tcdEx.acceptance.resultPh") },
    ],
  },
  {
    id: "check_facts",
    name: t("tcdEx.facts.name"),
    description: t("tcdEx.facts.desc"),
    icon: Search,
    color: "bg-amber-50 text-amber-600",
    prompts: [
      { key: "emotion", label: t("tcdEx.facts.emotion"), placeholder: t("tcdEx.facts.emotionPh") },
      { key: "trigger_facts", label: t("tcdEx.facts.triggerFacts"), placeholder: t("tcdEx.facts.triggerFactsPh") },
      { key: "interpretations", label: t("tcdEx.facts.interpretations"), placeholder: t("tcdEx.facts.interpretationsPh") },
      { key: "matches_facts", label: t("tcdEx.facts.matchesFacts"), placeholder: t("tcdEx.facts.matchesFactsPh") },
      { key: "opposite_action", label: t("tcdEx.facts.oppositeAction"), placeholder: t("tcdEx.facts.oppositeActionPh") },
    ],
  },
  {
    id: "dear_man",
    name: t("tcdEx.dear.name"),
    description: t("tcdEx.dear.desc"),
    icon: MessageCircle,
    color: "bg-green-50 text-green-600",
    prompts: [
      { key: "context", label: t("tcdEx.dear.context"), placeholder: t("tcdEx.dear.contextPh") },
      { key: "describe", label: t("tcdEx.dear.describe"), placeholder: t("tcdEx.dear.describePh"), hint: t("tcdEx.dear.describeHint") },
      { key: "express", label: t("tcdEx.dear.express"), placeholder: t("tcdEx.dear.expressPh"), hint: t("tcdEx.dear.expressHint") },
      { key: "assert", label: t("tcdEx.dear.assert"), placeholder: t("tcdEx.dear.assertPh"), hint: t("tcdEx.dear.assertHint") },
      { key: "reinforce", label: t("tcdEx.dear.reinforce"), placeholder: t("tcdEx.dear.reinforcePh"), hint: t("tcdEx.dear.reinforceHint") },
      { key: "mindful", label: t("tcdEx.dear.mindful"), placeholder: t("tcdEx.dear.mindfulPh"), hint: t("tcdEx.dear.mindfulHint") },
      { key: "confident", label: t("tcdEx.dear.confident"), placeholder: t("tcdEx.dear.confidentPh"), hint: t("tcdEx.dear.confidentHint") },
      { key: "negotiate", label: t("tcdEx.dear.negotiate"), placeholder: t("tcdEx.dear.negotiatePh"), hint: t("tcdEx.dear.negotiateHint") },
      { key: "debrief", label: t("tcdEx.dear.debrief"), placeholder: t("tcdEx.dear.debriefPh") },
    ],
  },
  {
    id: "give",
    name: t("tcdEx.give.name"),
    description: t("tcdEx.give.desc"),
    icon: Heart,
    color: "bg-pink-50 text-pink-600",
    prompts: [
      { key: "context", label: t("tcdEx.give.context"), placeholder: t("tcdEx.give.contextPh") },
      { key: "gentle", label: t("tcdEx.give.gentle"), placeholder: t("tcdEx.give.gentlePh"), hint: t("tcdEx.give.gentleHint") },
      { key: "interest", label: t("tcdEx.give.interest"), placeholder: t("tcdEx.give.interestPh"), hint: t("tcdEx.give.interestHint") },
      { key: "validate", label: t("tcdEx.give.validate"), placeholder: t("tcdEx.give.validatePh"), hint: t("tcdEx.give.validateHint") },
      { key: "easy", label: t("tcdEx.give.easy"), placeholder: t("tcdEx.give.easyPh"), hint: t("tcdEx.give.easyHint") },
      { key: "debrief", label: t("tcdEx.give.debrief"), placeholder: t("tcdEx.give.debriefPh") },
    ],
  },
  {
    id: "fast",
    name: t("tcdEx.fast.name"),
    description: t("tcdEx.fast.desc"),
    icon: Shield,
    color: "bg-orange-50 text-orange-600",
    prompts: [
      { key: "context", label: t("tcdEx.fast.context"), placeholder: t("tcdEx.fast.contextPh") },
      { key: "fair", label: t("tcdEx.fast.fair"), placeholder: t("tcdEx.fast.fairPh"), hint: t("tcdEx.fast.fairHint") },
      { key: "apologies", label: t("tcdEx.fast.apologies"), placeholder: t("tcdEx.fast.apologiesPh"), hint: t("tcdEx.fast.apologiesHint") },
      { key: "values", label: t("tcdEx.fast.values"), placeholder: t("tcdEx.fast.valuesPh"), hint: t("tcdEx.fast.valuesHint") },
      { key: "truthful", label: t("tcdEx.fast.truthful"), placeholder: t("tcdEx.fast.truthfulPh"), hint: t("tcdEx.fast.truthfulHint") },
      { key: "debrief", label: t("tcdEx.fast.debrief"), placeholder: t("tcdEx.fast.debriefPh") },
    ],
  },
];

const TcdExercisesPage = ({ user, logout }) => {
  const { t } = useI18n();
  const EXERCISES = getExercises(t);
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [entries, setEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/tcd-exercises`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSession = async () => {
    if (!selectedExercise) return;
    const filled = Object.values(responses).some(v => v.trim());
    if (!filled) { toast.error(t("exerciseForm.fillOne")); return; }
    try {
      const res = await axios.post(`${API}/tcd-exercises`, {
        exercise_type: selectedExercise.id,
        responses,
      }, { withCredentials: true });
      toast.success(t("exerciseForm.saved"));
      if (user?.role === "patient") promptShare(res.data, "tcd_exercises");
      setSelectedExercise(null);
      setResponses({});
      fetchEntries();
    } catch (e) { toast.error(t("exerciseForm.saveError")); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="tcd-exercises-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("tcdEx.pageTitle")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("tcdEx.pageSubtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("exerciseForm.sessions")}
            </span>
          )}
        </div>

        {selectedExercise ? (
          <Card className="max-w-2xl mx-auto" data-testid="tcd-exercise-form">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedExercise.icon className="w-5 h-5" />
                {selectedExercise.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{selectedExercise.description}</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {selectedExercise.prompts.map((prompt) => (
                <div key={prompt.key} className="space-y-1.5">
                  <label className="text-sm font-medium">{prompt.label}</label>
                  {prompt.hint && <p className="text-xs text-muted-foreground italic leading-relaxed">{prompt.hint}</p>}
                  <Textarea
                    placeholder={prompt.placeholder}
                    value={responses[prompt.key] || ""}
                    onChange={(e) => setResponses(prev => ({ ...prev, [prompt.key]: e.target.value }))}
                    rows={3}
                    data-testid={`input-${prompt.key}`}
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button onClick={saveSession} className="flex-1" data-testid="save-exercise-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> {t("exerciseForm.save")}
                </Button>
                <Button variant="outline" onClick={() => { setSelectedExercise(null); setResponses({}); }}>
                  {t("exerciseForm.back")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {EXERCISES.map((exercise) => (
                <Card key={exercise.id} className="card-hover cursor-pointer" onClick={() => setSelectedExercise(exercise)} data-testid={`card-${exercise.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${exercise.color} flex items-center justify-center mb-3 sm:mb-4`}>
                      <exercise.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="font-heading text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 break-words">{exercise.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">{exercise.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {entries.length > 0 && (
              <div>
                <Button variant="ghost" className="mb-3 text-sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
                  {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {t("exerciseForm.history")} ({entries.length})
                </Button>
                {showHistory && (
                  <div className="space-y-3">
                    {entries.slice(0, 10).map((entry, i) => {
                      const ex = EXERCISES.find(e => e.id === entry.exercise_type);
                      return (
                        <Card key={i} className="bg-muted/30">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-sm">{ex?.name || entry.exercise_type}</span>
                              <span className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleDateString("fr-FR")}</span>
                            </div>
                            {entry.responses && Object.entries(entry.responses).slice(0, 2).map(([k, v]) => (
                              <p key={k} className="text-xs text-muted-foreground truncate">{v}</p>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default TcdExercisesPage;
